import { useEffect, useState } from 'react';

import { DragToCloseHandlers, DragToCloseState } from './modal-v2.types';

/**
 * Hook to handle drag-to-close functionality for modals on mobile devices
 *
 * @param isOpen - Whether the modal is open
 * @param onClose - Callback to close the modal
 * @param enabled - Whether drag-to-close is enabled
 * @param threshold - Minimum drag distance to trigger close (in pixels)
 * @param closingDelay - Delay before calling onClose (in milliseconds)
 * @returns State and handlers for drag-to-close functionality
 */
export const useDragToClose = (
  isOpen: boolean,
  onClose: () => void,
  enabled: boolean = true,
  threshold: number = 100,
  closingDelay: number = 300
): DragToCloseState & DragToCloseHandlers => {
  const [startY, setStartY] = useState(0);
  const [currentTranslateY, setCurrentTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentTranslateY(0);
      setIsDragging(false);
      setIsClosing(false);
    }
  }, [isOpen]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!enabled) return;
    const touch = e.touches[0];
    setStartY(touch.clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!enabled || !isDragging) return;
    const touch = e.touches[0];
    const currentY = touch.clientY;
    const diffY = currentY - startY;

    // Only allow dragging down (positive Y direction)
    if (diffY > 0) {
      setCurrentTranslateY(diffY);
    }
  };

  const initiateClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, closingDelay);
  };

  const handleTouchEnd = () => {
    if (!enabled) return;

    // If dragged beyond threshold, close the modal
    if (currentTranslateY > threshold) {
      initiateClose();
    } else {
      // Otherwise, snap back to original position
      setCurrentTranslateY(0);
    }
    setIsDragging(false);
  };

  return {
    // State
    startY,
    currentTranslateY,
    isDragging,
    isClosing,
    // Handlers
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    initiateClose,
  };
};
